import moment from 'moment';
import {date} from 'yup';
import {ActionType} from '../../../components/AutoForm/AutoFieldNew';
import {hasAnyAnswer, hasSpecificAnswer, and} from '../../../components/Wizard/operators';
import {WizardField} from '../../../components/Wizard/QuestionWrapper';

import {ReviewDetailsFragment} from '../../../schema-types/ReviewDetailsFragment';
import {newUTCDate} from '../../../utils/newUTCDate';
import {precursorAndHeaderFieldsType} from './precursorFields';
import {AccountBasicFragment} from '../../../schema-types/AccountBasicFragment';
import {skippingAnnualReview} from './reviewAction';

const getBlock = (
  account: AccountBasicFragment,
  reviewDetails?: ReviewDetailsFragment
): WizardField<precursorAndHeaderFieldsType>[] => {
  const pastMeetingDate =
    reviewDetails?.dateBooked &&
    moment.utc(newUTCDate()).endOf('day').isAfter(reviewDetails?.dateBooked)
      ? reviewDetails?.dateBooked
      : undefined;
  const bookedMeetingDate = reviewDetails?.dateBooked;
  const plannedMeetingDate =
    !bookedMeetingDate && !pastMeetingDate ? reviewDetails?.dateScheduled : undefined;
  return [
    {
      id: 'meetingHappened',
      title: 'Has the meeting taken place?',
      question: '',
      label: '',
      enabled: (values) => !skippingAnnualReview(values, reviewDetails),
      // eslint-disable-next-line no-nested-ternary
      initialValue: pastMeetingDate ? 'Yes' : bookedMeetingDate ? 'No' : undefined,
      type: ActionType.buttonGroup,
      options: [
        {
          label: 'Yes',
          value: 'Yes',
        },
        {
          label: 'No',
          value: 'No',
        },
      ],
      requireSubmit: true,
    },
    {
      id: 'pastMeetingDate',
      title: 'Meeting date',
      question: 'When did the meeting happen?',
      label: 'Meeting date',
      enabled: hasSpecificAnswer<precursorAndHeaderFieldsType>('meetingHappened', 'Yes'),
      type: ActionType.datepicker,
      alwaysShowCalendar: true,
      requireSubmit: true,
      initialValue: pastMeetingDate ? String(pastMeetingDate) : undefined,
      validation: date().max(
        moment.utc(newUTCDate()).endOf('day').toDate(),
        'Date must be in the past'
      ),
    },
    {
      id: 'meetingBooked',
      title: 'Has the meeting been booked?',
      question: '',
      label: '',
      enabled: hasSpecificAnswer<precursorAndHeaderFieldsType>('meetingHappened', 'No'),
      type: ActionType.buttonGroup,
      initialValue: bookedMeetingDate ? 'Yes' : undefined,
      requireSubmit: true,
      options: [
        {
          label: 'Yes',
          value: 'Yes',
        },
        {
          label: 'No',
          value: 'No',
        },
      ],
    },
    {
      id: 'bookedMeetingDate',
      title: 'Booked date',
      question: 'When is the meeting?',
      label: 'Booked date',
      alwaysShowCalendar: true,
      enabled: and([
        hasSpecificAnswer<precursorAndHeaderFieldsType>('meetingHappened', 'No'),
        hasSpecificAnswer<precursorAndHeaderFieldsType>('meetingBooked', 'Yes'),
      ]),
      type: ActionType.datepicker,
      initialValue: bookedMeetingDate ? String(bookedMeetingDate) : undefined,
      requireSubmit: true,
      validation: date().min(newUTCDate(), 'Date must be in the future'),
    },
    {
      id: 'bookedMeetingTime',
      title: 'Booked time',
      question: '(Optional) What time is the meeting?',
      label: 'Booked time',
      enabled: and([
        hasAnyAnswer('bookedMeetingDate'),
        hasSpecificAnswer<precursorAndHeaderFieldsType>('meetingHappened', 'No'),
        hasSpecificAnswer<precursorAndHeaderFieldsType>('meetingBooked', 'Yes'),
      ]),
      type: ActionType.timepicker,
      initialValue: bookedMeetingDate ? String(bookedMeetingDate) : undefined,
      requireSubmit: true,
      canBeEmpty: true,
    },
    {
      id: 'plannedMeetingDate',
      title: 'Planned date',
      question: 'When is the meeting planned for?',
      label: 'Planned date',
      alwaysShowCalendar: true,
      enabled: and([
        hasSpecificAnswer<precursorAndHeaderFieldsType>('meetingHappened', 'No'),
        hasSpecificAnswer<precursorAndHeaderFieldsType>('meetingBooked', 'No'),
      ]),
      type: ActionType.datepicker,
      requireSubmit: true,
      initialValue: plannedMeetingDate ? String(plannedMeetingDate) : undefined,
      validation: date().min(newUTCDate(), 'Date must be in the future'),
    },
    {
      id: 'meetingType',
      title: 'Meeting Format',
      question: 'Please select the meeting format',
      label: '',
      enabled: (values) => !skippingAnnualReview(values, reviewDetails),
      initialValue: (reviewDetails ? reviewDetails.meetingType : undefined) || undefined,
      requireSubmit: true,
      type: ActionType.buttonGroup,
      options: [
        {
          label: 'Zoom',
          value: 'Web',
        },
        {
          label: 'Face to face',
          value: 'Face to face',
        },
        {
          label: 'Telephone',
          value: 'Telephone',
        },
        {
          label: 'Email',
          value: 'E-mail', //  NOTE: this is how it is named in Curo
        },
      ],
    },
    {
      id: 'meetingLocation',
      title: 'Meeting Location',
      question: 'Please select the meeting location',
      label: '',
      enabled: hasSpecificAnswer<precursorAndHeaderFieldsType>(
        'meetingFormat',
        'Face to face'
      ),
      type: ActionType.buttonGroup,
      requireSubmit: true,
      options: [
        {
          label: "Client's home",
          value: "Client's home",
        },
        {
          label: 'London office',
          value: 'London office',
        },
        {
          label: 'Fareham office',
          value: 'Fareham office',
        },
        {
          label: 'St Albans office',
          value: 'St Albans office',
        },
        {
          label: 'Bournemouth office',
          value: 'Bournemouth office',
        },
        {
          label: 'Other',
          value: 'Other',
        },
      ],
    },

    // {
    //   id: 'vulnerableForm',
    //   title: 'Client vulnerability',
    //   question: 'Does the client have any potential or temporary vulnerability?',
    //   label: '',
    //   enabled: () => true,
    //   type: ActionType.buttonGroup,
    //   initialValue: false,
    //   options: [
    //     {
    //       label: 'Yes',
    //       value: 'Yes',
    //     },
    //     {
    //       label: 'No',
    //       value: 'No',
    //     },
    //   ],
    // },
  ];
};

export default getBlock;
